import React, { useState } from 'react';
import SocialLinks from './SocialLinks';

const PRO_MODES = ['weighted', 'overdue'];

const MODE_INFO = {
  weighted: {
    label: 'Frequency weighted',
    math: 'P(x) ∝ count(x)',
    desc: 'Numbers with a higher historical draw count appear more often in the selection pool. Past frequency does not influence future lottery outcomes.',
  },
  overdue: {
    label: 'Gap based',
    math: 'P(x) ∝ Δt since x',
    desc: 'Selects from the 40% of numbers with the largest gap since their last draw. This reflects historical patterns only — each draw is an independent random event.',
  },
  random: {
    label: 'Pure random',
    math: 'P(x) = 1/N',
    desc: 'Every number has an equal chance of being selected. Uses a cryptographically secure RNG (CSPRNG). Statistically identical to the actual lottery draw mechanism.',
  },
};

export default function NumberPickerTab({ game, pickMode, setPickMode, picks, generatePick, animKey, isPro, onShowPaywall }) {
  const [copied, setCopied] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  const handleCopy = () => {
    if (!picks.main.length) return;
    const main = picks.main.join(' · ');
    const bonus = picks.bonus ? ` | ${game.bonusName}: ${picks.bonus}` : '';
    const text = `${game.name} picks: ${main}${bonus} — lottopickle.com`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      {/* Disclaimer banner */}
      <div className="pick-warning-banner">
        <span className="pick-warning-icon">⚠️</span>
        <span>
          <strong>Important:</strong> No selection method — including frequency-weighted or gap-based — can predict lottery outcomes.
          Every draw is an independent random event. Historical frequency does <em>not</em> improve your odds of winning.
          This tool is for data exploration and entertainment only.
        </span>
      </div>

      <div className="mode-row">
        {Object.entries(MODE_INFO).map(([m, info]) => {
          const locked = PRO_MODES.includes(m) && !isPro;
          return (
            <div key={m} className="mode-btn-wrap">
              <button
                className={`mode-btn ${pickMode === m ? 'active' : ''} ${locked ? 'mode-btn-locked' : ''}`}
                onClick={() => locked ? onShowPaywall() : setPickMode(m)}
              >
                {info.label}
                {locked && <span className="mode-lock"> 🔒</span>}
              </button>
              <button
                className="mode-info-btn"
                onClick={() => setTooltip(tooltip === m ? null : m)}
                aria-label={`Info about ${info.label}`}
              >
                ⓘ
              </button>
              {tooltip === m && (
                <div className="mode-tooltip">
                  <code className="mode-tooltip-math">{info.math}</code>
                  <p>{info.desc}</p>
                  <button className="mode-tooltip-close" onClick={() => setTooltip(null)}>✕</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="pick-section">
        <div className="pick-header">
          <h3>Your numbers</h3>
          <div className="pick-header-btns">
            <button className="regen-btn" onClick={generatePick}>
              ↻ Regenerate
            </button>
            <button className={`copy-btn ${copied ? 'copy-btn-done' : ''}`} onClick={handleCopy}>
              {copied ? '✓ Copied' : '⎘ Copy'}
            </button>
          </div>
        </div>
        <div className="pick-balls" key={animKey}>
          {picks.main.map((n, i) => (
            <div className="pick-ball" key={i} style={{ animationDelay: `${i * 60}ms` }}>{n}</div>
          ))}
          {picks.bonus && (
            <div
              className="pick-ball pick-bonus"
              style={{
                animationDelay: `${picks.main.length * 60}ms`,
                borderColor: game.bonusColor,
                background: `radial-gradient(circle at 38% 32%, ${game.bonusColor}99, ${game.bonusColor} 50%, ${game.bonusColor}dd)`,
                boxShadow: `0 0 20px ${game.bonusColor}88, 0 5px 15px rgba(0,0,0,0.4)`,
              }}
            >
              {picks.bonus}
            </div>
          )}
        </div>
        <div className="pick-legend">
          <span>White = main balls</span>
          <span style={{ color: game.bonusColor }}>● {game.bonusName}</span>
        </div>
      </div>

      <div className="pick-mode-desc">
        {MODE_INFO[pickMode]?.desc}
      </div>
      <p className="pick-disclaimer">Must be 18+. For entertainment and data analysis only. Not gambling advice.</p>

      <SocialLinks label="Follow for daily picks" variant="picker" />
    </>
  );
}
